import { useSentEmailListingStore } from './store';
import { useGetEmailSendingHistory } from '../../../query/mail';
import TablePagination from '../../../components/ui/TablePagination';
import styles from '../CSV/DisplayCSVPerformance.module.css';
import ReactTable from 'react-table';
import React, { useCallback, useMemo, useState } from 'react';
import { Button, Tag } from 'antd';
import moment from 'moment';
import EmailIDSearchBox from './Filter/EmailIDSearchBox';
import ReceiverSearchBox from './Filter/ReceiverSearchBox';
import useDebounce from 'react-use/lib/useDebounce';
import SenderSearchBox from './Filter/SenderSearchBox';
import TemplateFilter from './Filter/TemplateFilter';
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';
import ViewEmailButton from './ViewEmailButton';
import { useQueryClient } from '@tanstack/react-query';

const useDebouncedValue = value => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useDebounce(
    () => {
      setDebouncedValue(value);
    },
    500,
    [value],
  );
  return debouncedValue;
};

const useGetEmailSendingHistoryWithFilter = () => {
  const {
    page,
    limit,
    receiver,
    sender,
    startDate,
    endDate,
    selectedTemplates,
    emailId,
    setPage,
  } = useSentEmailListingStore();
  const debouncedEmailId = useDebouncedValue(emailId);
  const debouncedReceiver = useDebouncedValue(receiver);
  const debouncedSender = useDebouncedValue(sender);
  useDeepCompareEffect(() => {
    setPage(1);
  }, [
    debouncedReceiver,
    debouncedSender,
    startDate,
    endDate,
    selectedTemplates,
    debouncedEmailId,
  ]);
  return useGetEmailSendingHistory({
    page,
    limit,
    receiver: debouncedReceiver,
    sender: debouncedSender,
    startDate,
    endDate,
    templateIds: selectedTemplates.map(template => template.id),
    emailId: debouncedEmailId,
  });
};

export default function SentEmailHistory() {
  const { page, limit, setPage, setLimit } = useSentEmailListingStore();
  const {
    data: sentEmails = [],
    isLoading,
    refetch,
  } = useGetEmailSendingHistoryWithFilter();
  const columnDefs = useMemo(() => {
    return [
      {
        Header: () => {
          return (
            <>
              <p style={{ marginBottom: 5 }}>Email ID</p>
              <EmailIDSearchBox />
            </>
          );
        },
        id: 'emailId',
        accessor: 'emailId',
      },
      {
        Header: () => {
          return (
            <>
              <p style={{ marginBottom: 5 }}>Email Address</p>
              <ReceiverSearchBox />
            </>
          );
        },
        id: 'receiver',
        accessor: 'receiver',
      },
      {
        Header: () => {
          return (
            <>
              <p style={{ marginBottom: 5 }}>Sender</p>
              <SenderSearchBox />
            </>
          );
        },
        id: 'sender',
        accessor: 'sender',
      },
      {
        Header: () => {
          return (
            <>
              <p style={{ marginBottom: 5 }}>Template</p>
              <TemplateFilter />
            </>
          );
        },
        id: 'template.id',
        accessor: row => {
          if (!row.template) return 'N/A';
          return (
            <a
              href={`/mail/email-templates?id=${row.template.id}`}
              target="_blank"
            >
              {row.template.id} - {row.template.name}
            </a>
          );
        },
      },
      {
        Header: 'Delivered',
        id: 'delivered',
        accessor: row => {
          return (
            <Tag color={row.delivered ? 'green' : 'red'}>
              {row.delivered ? 'Yes' : 'No'}
            </Tag>
          );
        },
      },
      {
        Header: 'Email Sent On',
        id: 'createdDate',
        accessor: row => moment(row.createdDate).format('YYYY-MM-DD HH:mm'),
      },
      {
        Header: '',
        id: 'action',
        accessor: row => {
          return <ViewEmailButton id={row.id} />;
        },
      },
    ];
  }, []);
  const renderTable = useMemo(
    () => (
      <ReactTable
        PaginationComponent={TablePagination}
        className={`-highlight ${styles.innerTable}`}
        columns={columnDefs}
        data={sentEmails}
        firstText={<i className="material-icons">first_page</i>}
        lastText={<i className="material-icons">last_page</i>}
        loading={isLoading}
        manual
        minRows={0}
        nextText={<i className="material-icons">chevron_right</i>}
        page={page - 1}
        pageSize={limit}
        pageSizeOptions={[5, 10, 15, 20]}
        pages={1000}
        previousText={<i className="material-icons">chevron_left</i>}
        rowsSelectorText="Rows per page"
        showPageJump={false}
        showPaginationBottom
        sortable={false}
        onPageChange={nextPage => setPage(nextPage + 1)}
        onPageSizeChange={setLimit}
      />
    ),
    [columnDefs, sentEmails, isLoading],
  );
  return (
    <div className="space-y-4">
      <Button
        icon="reload"
        loading={isLoading}
        onClick={() => {
          refetch({});
        }}
        // onClick={props.onRefresh}
      >
        Refresh
      </Button>
      {renderTable}
    </div>
  );
}
